import { config } from '../config/environment';
import { logger } from './logger';

const CLIENTS_SERVICE_URL = config.clientsApiBase || 'http://localhost:7077';

const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with clients-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

// Company API functions
export const fetchCompanies = async (options = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.pageSize) queryParams.append('pageSize', options.pageSize);
    if (options.isPublic !== undefined) queryParams.append('isPublic', options.isPublic);
    if (options.industry) queryParams.append('industry', options.industry);
    if (options.ownerId) queryParams.append('ownerId', options.ownerId);

    const response = await fetch(`${CLIENTS_SERVICE_URL}companies?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch companies: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching companies:', error);
    throw error;
  }
};

export const fetchUserCompanies = async (options = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.pageSize) queryParams.append('pageSize', options.pageSize);
    if (options.isPublic !== undefined) queryParams.append('isPublic', options.isPublic);
    if (options.industry) queryParams.append('industry', options.industry);

    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/user/list?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user companies: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    logger.error('Error fetching user companies:', error);
    throw error;
  }
};

export const fetchCompanyById = async (companyId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch company: ${response.status}`);
    }

    const result = await response.json();
    return result.company;
  } catch (error) {
    logger.error('Error fetching company:', error);
    throw error;
  }
};

export const createCompany = async (companyData) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create company: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error creating company:', error);
    throw error;
  }
};

export const updateCompany = async (companyId, companyData) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update company: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error updating company:', error);
    throw error;
  }
};

export const deleteCompany = async (companyId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete company: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error deleting company:', error);
    throw error;
  }
};

export const joinCompany = async (companyId, role = 'employee') => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}/join`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      throw new Error(`Failed to join company: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error joining company:', error);
    throw error;
  }
};

export const fetchJoinRequests = async (companyId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}/join-requests`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch join requests: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching join requests:', error);
    throw error;
  }
};

export const approveJoinRequest = async (companyId, userId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}/join-requests/${userId}/approve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to approve join request: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error approving join request:', error);
    throw error;
  }
};

export const rejectJoinRequest = async (companyId, userId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}/join-requests/${userId}/reject`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to reject join request: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error rejecting join request:', error);
    throw error;
  }
};
